/**
 * GA4 with Consent Mode v2.
 *
 * gtag.js is not loaded at all until the visitor explicitly accepts in the
 * cookie banner, so visitors who decline or ignore it never contact Google and
 * never receive an analytics cookie. EthosHub sells workplace compliance, so
 * implied consent is not an option under UK GDPR / PECR.
 *
 * Set REACT_APP_GA4_ID (G-XXXXXXXXXX) in the Amplify environment variables.
 * With it unset every function here is a no-op, so local and preview builds
 * send nothing.
 */

const GA_ID = process.env.REACT_APP_GA4_ID;
const CONSENT_KEY = "ethoshub_cookie_consent";

// react-snap prerenders each route in headless Chromium at build time. Without
// this guard every build would fire a batch of page_view events.
export const isPrerender = () =>
  typeof navigator !== "undefined" && navigator.userAgent === "ReactSnap";

export const analyticsEnabled = () => Boolean(GA_ID) && !isPrerender();

export function readConsent() {
  try {
    const v = localStorage.getItem(CONSENT_KEY);
    if (v === "granted" || v === "true") return "granted"; // "true" = pre-existing value
    if (v === "denied") return "denied";
    return null;
  } catch {
    return null; // private browsing / storage blocked
  }
}

export function writeConsent(value) {
  try {
    localStorage.setItem(CONSENT_KEY, value);
  } catch {
    /* storage unavailable; consent simply won't persist */
  }
}

function gtag() {
  window.dataLayer = window.dataLayer || [];
  // eslint-disable-next-line prefer-rest-params
  window.dataLayer.push(arguments);
}

/**
 * Loads gtag.js. Only ever called once consent is granted, so nothing is
 * requested from Google and no cookies exist for visitors who decline or who
 * never answer the banner.
 */
function loadGtag() {
  if (window.__ethoshubGaLoaded) return;
  window.__ethoshubGaLoaded = true;

  window.gtag = gtag;
  gtag("js", new Date());

  // Consent Mode v2 is still declared, so gtag behaves correctly if a future
  // change loads it earlier.
  gtag("consent", "default", {
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
    analytics_storage: "denied",
  });
  gtag("consent", "update", { analytics_storage: "granted" });

  // page_view is sent manually on route change instead, so the SPA reports
  // real navigations rather than only the first load.
  gtag("config", GA_ID, { send_page_view: false, anonymize_ip: true });

  const s = document.createElement("script");
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
  document.head.appendChild(s);
}

export function initAnalytics() {
  if (!analyticsEnabled()) return;
  // Returning visitors who already accepted get analytics on first paint.
  if (readConsent() === "granted") loadGtag();
}

export function grantConsent() {
  writeConsent("granted");
  if (!analyticsEnabled()) return;
  loadGtag();
}

export function denyConsent() {
  writeConsent("denied");
  // gtag.js was never loaded, so there is nothing to switch off.
  if (window.gtag) window.gtag("consent", "update", { analytics_storage: "denied" });
}

export function trackPageView(path) {
  if (!analyticsEnabled() || readConsent() !== "granted") return;
  gtag("event", "page_view", {
    page_path: path,
    page_location: window.location.href,
    page_title: document.title,
  });
}
